import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import Spinner from 'react-bootstrap/Spinner';

import { URLS } from '../../resources/constants';
import { useForecastApi } from '../../resources/hooks/api/useForecastApiHook';
import SAlert from '../../components/SAlert';
import ForecastSetupModal, { ForecastSetupValues } from './ForecastSetupModal';
import LineForm from './LineForm';
import MonthlyValuesModal from './MonthlyValuesModal';
import ForecastLayout from './ForecastLayout';
import { METHOD_LABELS, MONTH_FULL_NAMES, lineSpan, summarizeParams } from './forecastUtils';

/**
 * Forecast Entry: one tab per COA category; each tab adds account lines with a
 * forecast method and its assumptions. Only assumptions are saved here — the
 * calculated five-year results come from the calculation engine (next step).
 */
const ForecastEntry: React.FC = () => {
    const { t } = useTranslation('scenes.FiveYearForecast');
    const navigate = useNavigate();
    const { forecastId } = useParams();
    const id = Number(forecastId);

    const api = useForecastApi();
    const [forecast, setForecast] = useState<any>(null);
    const [categories, setCategories] = useState<any[]>([]);
    const [tabIndex, setTabIndex] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [editing, setEditing] = useState<any>(null);
    const [monthlyLine, setMonthlyLine] = useState<any>(null);
    const [toDelete, setToDelete] = useState<any>(null);
    const [showSetup, setShowSetup] = useState<boolean>(false);
    const [showReview, setShowReview] = useState<boolean>(false);
    const [savedMessage, setSavedMessage] = useState<string>('');
    // Bumped on each successful save so LineForm clears all inputs (esp. add -> add).
    const [resetToken, setResetToken] = useState<number>(0);

    const readOnly = forecast?.status === 'final';
    const category = categories[tabIndex];
    const linesByCategory = useMemo(() => {
        const map: Record<string, any[]> = {};
        (forecast?.lines || []).forEach((l: any) => { (map[l.category] = map[l.category] || []).push(l); });
        return map;
    }, [forecast]);
    const tabLines = category ? (linesByCategory[category.key] || []) : [];

    const fail = (msg: string) => {
        toast.error(msg);
        setSaving(false);
    };

    const loadForecast = (then?: (f: any) => void) => {
        api.getForecast(id, (_m: string, resp: any) => {
            const f = resp?.data?.data;
            setForecast(f);
            then && then(f);
        }, (msg: string) => {
            toast.error(msg);
            navigate(URLS.FIVE_YEAR_FORECAST);
        });
    };

    useEffect(() => {
        if (!id) { navigate(URLS.FIVE_YEAR_FORECAST); return; }
        setLoading(true);
        loadForecast((f) => {
            api.getCategories(f.location_branch_id ? { branch_id: f.location_branch_id } : {},
                (_m: string, resp: any) => {
                    setCategories(resp?.data?.data?.categories || []);
                    setLoading(false);
                }, (msg: string) => { toast.error(msg); setLoading(false); });
        });
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const selectTab = (i: number) => {
        setTabIndex(i);
        setEditing(null);
        setSavedMessage('');
    };

    // "Edit Entries" on a detail page deep-links here with ?tab=<category-key>
    // (e.g. People & Payroll -> ?tab=payroll); open that tab instead of the first.
    const [searchParams] = useSearchParams();
    const tabKey = searchParams.get('tab');
    useEffect(() => {
        if (!tabKey || !categories.length) return;
        const i = categories.findIndex((c) => c.key === tabKey);
        if (i >= 0 && i !== tabIndex) selectTab(i);
    }, [categories, tabKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const saveLine = (body: any) => {
        setSaving(true);
        const wasEditing = !!editing;
        const done = (msg: string, _resp?: any) => {
            toast.success(msg);
            setSaving(false);
            setSavedMessage(wasEditing ? 'updated' : 'added');
            setEditing(null);
            setResetToken((n) => n + 1);
            loadForecast();
        };
        if (editing) {
            api.updateLine(id, editing.id, body, (msg: string, resp: any) => {
                // Monthly values staged on the form (Direct Monthly pop-up) save separately on updates.
                if (Array.isArray(body.monthly_values) && body.monthly_values.length) {
                    api.saveMonthlyValues(id, editing.id, body.monthly_values, done, fail);
                } else {
                    done(msg, resp);
                }
            }, fail);
        } else {
            api.addLine(id, body, done, fail);
        }
    };

    const saveMonthly = (values: { month_index: number; amount: number | null }[], units: Record<string, number> | null) => {
        if (!values.length && !units) { setMonthlyLine(null); return; }
        const line = monthlyLine;
        const finish = (msg: string) => {
            toast.success(msg);
            setSaving(false);
            setMonthlyLine(null);
            loadForecast();
        };
        const saveAmounts = (msg: string) => {
            if (!values.length) return finish(msg);
            api.saveMonthlyValues(id, line.id, values, finish, fail);
        };
        setSaving(true);
        if (units) {
            // Typed units live in the line's params (Units × Price lines).
            const params = { ...(line.params || {}), units_by_month: units };
            if (!Object.keys(units).length) delete params.units_by_month;
            api.updateLine(id, line.id, { params }, saveAmounts, fail);
        } else {
            saveAmounts('');
        }
    };

    const removeLine = () => {
        const line = toDelete;
        setToDelete(null);
        api.deleteLine(id, line.id, (msg: string) => {
            toast.success(msg);
            if (editing?.id === line.id) setEditing(null);
            loadForecast();
        }, (msg: string) => toast.error(msg));
    };

    const setStatus = (status: 'draft' | 'final') => {
        api.updateForecast(id, { status }, (msg: string, resp: any) => {
            toast.success(msg);
            setForecast((f: any) => ({ ...f, ...(resp?.data?.data || { status }) }));
        }, (msg: string) => toast.error(msg));
    };

    const saveSetup = (values: ForecastSetupValues) => {
        setSaving(true);
        api.updateForecast(id, values, (msg: string) => {
            toast.success(msg);
            setSaving(false);
            setShowSetup(false);
            loadForecast();
        }, fail);
    };

    if (loading || !forecast) {
        return (
            <ForecastLayout active="entry" forecastId={id}>
                <div className="fyf-empty"><Spinner animation="border" /></div>
            </ForecastLayout>
        );
    }

    const totalLines = (forecast.lines || []).length;

    return (
        <ForecastLayout active="entry" forecastId={id}>
            <div className="d-flex justify-content-between align-items-start gap-3">
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <button type="button" className="fyf-back" onClick={() => navigate(URLS.FIVE_YEAR_FORECAST)}>
                        ← {t('entry.back', 'All forecasts')}
                    </button>
                    <h1>{t('entry.title', 'Forecast Entry')}</h1>
                    <div className="fyf-sub mb-1">{t('entry.subtitle', 'COA-driven forecast assumptions and monthly data entry')}</div>
                    <div className="fyf-meta">
                        <strong className="text-dark text-break">{forecast.name}</strong>{' '}
                        <span className={`fyf-badge ${readOnly ? 'final' : 'draft'}`}>
                            {readOnly ? t('status.final', 'Final') : t('status.draft', 'Draft')}
                        </span>
                        {' · '}{forecast.forecast_type === 'new_location'
                            ? t('type.new_location', 'New location') : t('type.new_business', 'New business')}
                        {' · '}{t('entry.starts', 'Starts {{month}} {{year}}', { month: MONTH_FULL_NAMES[forecast.start_month - 1], year: forecast.start_year })}
                        {' · '}{forecast.year_type === 'fiscal' ? t('setup.fiscal', 'Fiscal year') : t('setup.calendar', 'Calendar year')}
                        {Number(forecast.opening_cash || 0) !== 0 && (
                            <>{' · '}{t('entry.startingCash', 'Starting cash')}: ${Number(forecast.opening_cash).toLocaleString()}</>
                        )}
                    </div>
                </div>
                <div className="fyf-actions mt-0 flex-shrink-0 justify-content-end">
                    <button type="button" className="fyf-secondary"
                        onClick={() => navigate(URLS.FIVE_YEAR_FORECAST_FIVE_YEAR_VIEW.replace(':forecastId', String(id)))}>
                        {t('entry.viewResults', 'View Five-Year Results')}
                    </button>
                    {!readOnly && (
                        <button type="button" className="fyf-secondary" onClick={() => setShowSetup(true)}>
                            {t('entry.editSetup', 'Edit Setup')}
                        </button>
                    )}
                    {readOnly ? (
                        <button type="button" className="fyf-secondary" onClick={() => setStatus('draft')}>
                            {t('entry.backToDraft', 'Back to Draft')}
                        </button>
                    ) : (
                        <button type="button" className="fyf-primary" onClick={() => setStatus('final')}
                            disabled={totalLines === 0}
                            title={totalLines === 0
                                ? t('entry.needLineToFinalize', 'Add at least one account to the forecast before marking it final.')
                                : undefined}>
                            {t('entry.markFinal', 'Mark as Final')}
                        </button>
                    )}
                </div>
            </div>

            {readOnly && (
                <div className="fyf-note">
                    {t('entry.finalNotice', 'This forecast is final and read-only. Move it back to draft to make changes.')}
                </div>
            )}

            {/* Category tabs */}
            <div className="fyf-tabs">
                {categories.map((c, i) => {
                    const count = (linesByCategory[c.key] || []).length;
                    return (
                        <button key={c.key} type="button" className={i === tabIndex ? 'on' : ''} onClick={() => selectTab(i)}>
                            {String(t(`category.${c.key}`, c.label))}
                            {count ? <span className="fyf-count">{count}</span> : null}
                        </button>
                    );
                })}
            </div>

            {category && (
                <>
                    <div className="fyf-coa">
                        <div className="fyf-coa-title">
                            {category.header_account ? `${category.header_account} — ` : ''}
                            {category.header_name || String(t(`category.${category.key}`, category.label))}
                        </div>
                        <div>
                            {category.coa_driven
                                ? t('entry.coaNote', 'Available forecast accounts are pulled from the REVREX General Ledger Master COA.')
                                : t('entry.cashNote', 'Enter start-up funding and financing: loans, line of credit, owner capital, SAFE, principal payments, capital expenditures, intangible purchases, asset sales, leases and dividends. Interest goes on the Interest tab.')}
                            {' '}{String(t(`rule.${category.key}`, category.presentation_rule))}.
                        </div>
                    </div>

                    <LineForm
                        category={category}
                        forecast={forecast}
                        editing={editing}
                        readOnly={readOnly}
                        saving={saving}
                        savedMessage={savedMessage}
                        resetToken={resetToken}
                        onSubmit={saveLine}
                        onCancelEdit={() => setEditing(null)}
                        onClear={() => setSavedMessage('')}
                    />

                    {/* Lines included in this category */}
                    <div className="fyf-panel">
                        <h3>{t('entry.included', 'Accounts Included in Forecast')}</h3>
                        {tabLines.length === 0 ? (
                            <div className="fyf-meta">{t('entry.noLines', 'Nothing added in this category yet.')}</div>
                        ) : (
                            <div className="fyf-table-wrap">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>{t('entry.colAccount', 'GL COA')}</th>
                                            <th>{t('entry.colDescription', 'Description / Product')}</th>
                                            <th>{t('entry.colMethod', 'Method')}</th>
                                            <th>{t('entry.colPeriod', 'Months')}</th>
                                            <th>{t('entry.colAssumptions', 'Assumptions')}</th>
                                            <th>{t('entry.colStatus', 'Status')}</th>
                                            <th />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tabLines.map((l: any) => (
                                            <tr key={l.id} className={editing?.id === l.id ? 'fyf-row-active' : ''}>
                                                <td className="text-nowrap">{l.account_number || '—'}</td>
                                                <td>
                                                    {l.account_name}{l.description ? ` / ${l.description}` : ''}
                                                    {!l.master_chart_of_account_id && category.coa_driven && (
                                                        <> <span className="fyf-badge">{t('entry.forecastOnly', 'forecast-only')}</span></>
                                                    )}
                                                </td>
                                                <td>{String(t(`method.${l.method}`, (METHOD_LABELS as any)[l.method] || l.method))}</td>
                                                <td className="text-nowrap">{lineSpan(l, forecast.start_month, forecast.start_year)}</td>
                                                <td>
                                                    {summarizeParams(l.method, l.params, forecast.lines)}
                                                    {(l.monthly_values || []).length ? (
                                                        <div className="fyf-meta">
                                                            {t('entry.typedMonths', '{{n}} month(s) entered directly', { n: l.monthly_values.length })}
                                                        </div>
                                                    ) : null}
                                                    {!summarizeParams(l.method, l.params, forecast.lines) && !(l.monthly_values || []).length ? '—' : null}
                                                </td>
                                                <td><span className="fyf-badge">{t('entry.statusIncluded', 'Included')}</span></td>
                                                <td className="text-end text-nowrap">
                                                    {!readOnly && (
                                                        <>
                                                            <button type="button" className="fyf-secondary fyf-sm me-1"
                                                                onClick={() => { setEditing(l); if (l.method === 'direct_monthly') setMonthlyLine(l); }}>
                                                                {t('entry.edit', 'Edit')}
                                                            </button>
                                                            <button type="button" className="fyf-danger fyf-sm" onClick={() => setToDelete(l)}>
                                                                {t('entry.remove', 'Remove')}
                                                            </button>
                                                        </>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    <div className="fyf-actions">
                        <button type="button" className="fyf-secondary" disabled={tabIndex === 0} onClick={() => selectTab(tabIndex - 1)}>
                            {t('entry.prev', 'Previous Category')}
                        </button>
                        <button type="button" className="fyf-primary" disabled={tabIndex >= categories.length - 1}
                            onClick={() => selectTab(tabIndex + 1)}>
                            {t('entry.next', 'Next Category')}
                        </button>
                        <button type="button" className="fyf-secondary" onClick={() => setShowReview((v) => !v)}>
                            {t('entry.review', 'Review Forecast Entry')}
                        </button>
                    </div>

                    {showReview && (
                        <div className="fyf-panel mt-3">
                            <h3>{t('entry.reviewTitle', 'Forecast Entry Review')}</h3>
                            <p className="fyf-meta mt-0">
                                {t('entry.reviewHint', 'Accounts entered in each category and how the forecast engine will treat them.')}
                            </p>
                            <table>
                                <thead>
                                    <tr>
                                        <th>{t('entry.reviewCategory', 'Entry Category')}</th>
                                        <th>{t('entry.reviewCount', 'Accounts Included')}</th>
                                        <th>{t('entry.reviewRule', 'Presentation Rule')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {categories.map((c, i) => (
                                        <tr key={c.key}>
                                            <td>
                                                <button type="button" className="fyf-link" onClick={() => selectTab(i)}>
                                                    {String(t(`category.${c.key}`, c.label))}
                                                </button>
                                            </td>
                                            <td>{(linesByCategory[c.key] || []).length}</td>
                                            <td>{String(t(`rule.${c.key}`, c.presentation_rule))}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <div className="fyf-saved">
                                <b>{t('entry.reviewSaved', 'All entries are saved.')}</b>{' '}
                                {t('entry.reviewSavedHint', '{{n}} account(s) across all categories. These assumptions will flow to the Five-Year View, Monthly View, detail reports, Dashboard and Cash Flow once forecast calculations are enabled.', { n: totalLines })}
                            </div>
                        </div>
                    )}
                </>
            )}

            <MonthlyValuesModal
                show={!!monthlyLine}
                line={monthlyLine}
                forecast={forecast}
                readOnly={readOnly}
                saving={saving}
                onClose={() => setMonthlyLine(null)}
                onSave={saveMonthly}
            />

            <ForecastSetupModal
                show={showSetup}
                title={t('setup.editTitle', 'Edit Forecast Setup')}
                saving={saving}
                initial={{
                    name: forecast.name,
                    description: forecast.description || '',
                    start_year: forecast.start_year,
                    start_month: forecast.start_month,
                    year_type: forecast.year_type,
                    forecast_type: forecast.forecast_type || 'new_business',
                    opening_cash: Number(forecast.opening_cash) || 0,
                }}
                onClose={() => setShowSetup(false)}
                onSave={saveSetup}
            />

            <SAlert
                show={!!toDelete}
                title={t('entry.removeTitle', 'Remove this account?')}
                msg={t('entry.removeMsg', '"{{name}}" will be removed from the forecast.', { name: toDelete?.account_name || '' })}
                icon="warning"
                showCancel
                confirmBtnText={t('entry.remove', 'Remove')}
                cancelBtnText={t('common.cancel', 'Cancel')}
                confirmBtnBsStyle="danger"
                onConfirm={removeLine}
                onCancel={() => setToDelete(null)}
            />
        </ForecastLayout>
    );
};

export default ForecastEntry;
